import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/auth";
import { BrandLogo } from "@/logos";
import {
  HardDrive, Upload, Download, Trash2, Search, FileArchive, Loader2, Cpu, Package,
} from "lucide-react";
import Pilotes from "@/pages/Pilotes";

const KINDS = ["DA", "AUTH", "LOADER", "SCATTER", "FIRMWARE", "TOOL"];
const PLATFORMS = ["mediatek", "qualcomm", "unisoc", "infinity", "ios", "android", "general"];
const BRANDS = [
  "apple","samsung","xiaomi","huawei","oppo","realme","oneplus","tecno",
  "infinix","itel","nokia","motorola","wiko","lg","zte","sony","pixel","docomo",
];

// Magic bytes / patterns to identify chipset from a file. Returns { chipset, kind, note }.
const MAGICS = [
  { needle: "MTK_BLOADER_INFO", chipset: "MediaTek Loader", kind: "LOADER" },
  { needle: "MTK_ALL_IN_ONE_LOADER", chipset: "MediaTek DA", kind: "DA" },
  { needle: "preloader_", chipset: "MediaTek Preloader", kind: "LOADER" },
  { needle: "MTK_AllInOne_DA_v5", chipset: "MediaTek DA v5", kind: "DA" },
  { needle: "SecureBoot Description", chipset: "MediaTek Auth", kind: "AUTH" },
  { needle: "sprd-loader", chipset: "Unisoc / Spreadtrum SPD", kind: "LOADER" },
  { needle: "SP Flash Tool", chipset: "MediaTek SP Tool", kind: "TOOL" },
  { needle: "programmer_ufs", chipset: "Qualcomm UFS Programmer", kind: "LOADER" },
  { needle: "prog_firehose", chipset: "Qualcomm Firehose", kind: "LOADER" },
  { needle: "QDLoader", chipset: "Qualcomm 9008 Loader", kind: "LOADER" },
  { needle: "iBoot", chipset: "Apple iBoot", kind: "FIRMWARE" },
  { needle: "AppleAP", chipset: "Apple SoC", kind: "FIRMWARE" },
];
const HEX_MAGICS = [
  { hex: "504b0304", chipset: "Archive ZIP (Firmware pack)", kind: "FIRMWARE" }, // PK\x03\x04
  { hex: "1f8b08", chipset: "GZIP (payload)", kind: "FIRMWARE" },
  { hex: "68737170", chipset: "SquashFS (system.img)", kind: "FIRMWARE" }, // "hsqp"
  { hex: "414e4452", chipset: "Android Boot Image", kind: "FIRMWARE" }, // "ANDR"
  { hex: "53414d5355", chipset: "Samsung PIT/ODIN", kind: "FIRMWARE" }, // "SAMSU"
];

async function detectChipset(file) {
  const size = Math.min(file.size, 65536);
  const buf = await file.slice(0, size).arrayBuffer();
  const bytes = new Uint8Array(buf);
  // hex header
  let hexHead = "";
  for (let i = 0; i < Math.min(8, bytes.length); i++) hexHead += bytes[i].toString(16).padStart(2, "0");
  const hexMatch = HEX_MAGICS.find(m => hexHead.startsWith(m.hex));
  // ASCII scan
  const asStr = new TextDecoder("latin1").decode(bytes);
  const strMatch = MAGICS.find(m => asStr.includes(m.needle));
  const m = strMatch || hexMatch;
  return {
    chipset: m?.chipset || "Format non identifié",
    kind: m?.kind || null,
    identified: !!m,
    hex_header: hexHead.toUpperCase(),
  };
}

export default function FirmwarePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const licActive = user?.license_status === "active";
  const [tab, setTab] = useState("firmware");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("");
  const [brand, setBrand] = useState("");
  const [kind, setKind] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [form, setForm] = useState({ brand: "samsung", platform: "qualcomm", kind: "FIRMWARE", model: "", notes: "" });
  const [msg, setMsg] = useState(null);
  const [detected, setDetected] = useState(null);

  const onFileChange = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { setDetected(null); return; }
    const info = await detectChipset(file);
    setDetected({ ...info, filename: file.name, size: file.size });
    if (info.kind && !form.kind) setForm({ ...form, kind: info.kind });
  };

  const load = async () => {
    const params = new URLSearchParams();
    if (platform) params.set("platform", platform);
    if (brand) params.set("brand", brand);
    if (kind) params.set("kind", kind);
    const { data } = await axios.get(`${API}/firmware?${params}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [platform, brand, kind]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter(r =>
      [r.filename, r.brand, r.model, r.notes, r.kind, r.platform]
        .some(v => (v || "").toString().toLowerCase().includes(s))
    );
  }, [rows, q]);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { setMsg({ ok: false, text: "Sélectionnez un fichier" }); return; }
    setUploading(true); setMsg(null);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    try {
      await axios.post(`${API}/firmware/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ ok: true, text: "Fichier ajouté à la base." });
      fileRef.current.value = "";
      load();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Échec du téléversement" });
    } finally { setUploading(false); }
  };

  const download = async (r) => {
    try {
      const resp = await axios.get(`${API}/firmware/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = r.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.detail ? await err.response.data.text() : "Téléchargement refusé");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement ?")) return;
    await axios.delete(`${API}/firmware/${id}`); load();
  };

  return (
    <div className="p-6 space-y-6" data-testid="firmware-page">
      <div className="card-lhs p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
            <HardDrive className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono-tech">Base firmware & pilotes</div>
            <h1 className="font-display text-3xl font-extrabold">Firmware & Pilotes</h1>
            <p className="text-zinc-400 text-sm mt-1">
              Fichiers firmware (DA/Auth/Loader) + pilotes USB nécessaires à LHSunlocker Desktop.
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-zinc-800" data-testid="firmware-tabs">
        <button
          onClick={() => setTab("firmware")}
          data-testid="tab-firmware"
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 ${
            tab === "firmware" ? "border-orange-500 text-orange-400" : "border-transparent text-zinc-500 hover:text-white"
          }`}>
          <FileArchive className="w-4 h-4" /> Firmware
        </button>
        <button
          onClick={() => setTab("pilotes")}
          data-testid="tab-pilotes"
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 ${
            tab === "pilotes" ? "border-orange-500 text-orange-400" : "border-transparent text-zinc-500 hover:text-white"
          }`}>
          <Package className="w-4 h-4" /> Pilotes
        </button>
      </div>

      {tab === "pilotes" && <Pilotes />}

      {tab === "firmware" && (<></>)}
      {tab === "firmware" && (<>

      {isAdmin && (
        <div className="card-lhs p-5" data-testid="firmware-upload-card">
          <h2 className="font-display font-bold uppercase tracking-wider text-sm mb-4">Ajouter un fichier</h2>
          <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <select data-testid="fw-brand" className="input-lhs md:col-span-1" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}>
              {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select data-testid="fw-platform" className="input-lhs md:col-span-1" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="fw-kind" className="input-lhs md:col-span-1" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <input data-testid="fw-model" className="input-lhs md:col-span-2" placeholder="Modèle (ex: SM-A546B)" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            <input data-testid="fw-file" ref={fileRef} type="file" onChange={onFileChange} className="input-lhs md:col-span-1 text-xs" />
            <input data-testid="fw-notes" className="input-lhs md:col-span-5" placeholder="Notes (ex: Android 13 · fichier officiel)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <button data-testid="fw-upload-btn" disabled={uploading} className="btn-orange rounded-lg md:col-span-1 flex items-center justify-center gap-2 text-sm">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Envoyer
            </button>
          </form>
          {msg && (
            <div className={`mt-3 text-sm px-3 py-2 rounded-lg border ${msg.ok ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-red-400 border-red-500/40 bg-red-500/10"}`}>
              {msg.text}
            </div>
          )}
          {detected && (
            <div data-testid="detected-preview" className={`mt-3 rounded-lg border p-3 text-xs font-mono-tech ${detected.identified ? "border-orange-500/40 bg-orange-500/5 text-orange-300" : "border-zinc-800 bg-[#0C0C10] text-zinc-400"}`}>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Aperçu du fichier</div>
              <div><span className="text-zinc-500">Fichier:</span> {detected.filename} ({(detected.size/(1024*1024)).toFixed(1)} MB)</div>
              <div><span className="text-zinc-500">En-tête HEX:</span> {detected.hex_header}</div>
              <div><span className="text-zinc-500">Chipset détecté:</span> <span className="text-white">{detected.chipset}</span></div>
              {detected.kind && <div><span className="text-zinc-500">Type suggéré:</span> <span className="text-white">{detected.kind}</span></div>}
            </div>
          )}
          <p className="text-[11px] text-zinc-500 mt-2">Taille max 200 MB · stocké dans MongoDB GridFS.</p>
        </div>
      )}

      <div className="card-lhs p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="fw-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Rechercher (marque, modèle, fichier)..." className="input-lhs pl-9" />
          </div>
          <select data-testid="fw-filter-platform" className="input-lhs w-40" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="">Toutes plateformes</option>
            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="fw-filter-brand" className="input-lhs w-40" value={brand} onChange={(e) => setBrand(e.target.value)}>
            <option value="">Toutes marques</option>
            {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="fw-filter-kind" className="input-lhs w-32" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Tous types</option>
            {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="firmware-list">
          {filtered.length === 0 && (
            <div className="col-span-full py-10 text-center text-sm text-zinc-500">Aucun fichier pour ces filtres.</div>
          )}
          {filtered.map(r => (
            <div key={r.id} className="card-lhs p-4 flex flex-col gap-3" data-testid={`fw-item-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BrandLogo id={r.brand} size={22} />
                  <span className="brand-pill text-orange-400 !text-[10px]">{r.kind}</span>
                  <span className="text-[10px] text-zinc-500 font-mono-tech uppercase">{r.platform}</span>
                </div>
                {isAdmin && (
                  <button onClick={() => del(r.id)} className="text-zinc-500 hover:text-red-400" title="Supprimer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div>
                <div className="text-sm font-semibold flex items-center gap-2 truncate">
                  <FileArchive className="w-4 h-4 text-orange-500 shrink-0" />
                  <span className="truncate">{r.filename}</span>
                </div>
                {r.model && <div className="text-[11px] text-zinc-400 mt-0.5 font-mono-tech flex items-center gap-1"><Cpu className="w-3 h-3" /> {r.model}</div>}
                {r.notes && <div className="text-[11px] text-zinc-500 mt-1">{r.notes}</div>}
              </div>
              <div className="flex items-center justify-between mt-auto pt-2 border-t border-zinc-800">
                <span className="text-[10px] text-zinc-500 font-mono-tech">
                  {(r.size_bytes / (1024*1024)).toFixed(1)} MB · {r.download_count || 0} téléchargements
                </span>
                <button
                  data-testid={`fw-dl-${r.id}`}
                  onClick={() => download(r)}
                  disabled={!licActive}
                  title={!licActive ? "Licence active requise" : "Télécharger"}
                  className="btn-orange px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 disabled:opacity-40"
                >
                  <Download className="w-3.5 h-3.5" /> Télécharger
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      </>)}
    </div>
  );
}
